import {
  withStyles,
  chooseColor,
  InteracWordmark,
  ConvenienceFeeRestriction,
  ELinxConnectionMethod,
  BankingPaymentRail,
  BodyText,
  Term,
} from 'vopay';
import { ComponentProps, ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useLayout } from '@/providers/Layout.provider';
import { useToken } from '@/providers/Token.provider';
import { usePaylink, Flow } from '@/providers/Paylink.provider';
import GooglePayButton from '@/components/Google/GooglePayButton';
import ApplePayButton from '@/components/Apple/ApplePayButton';
import useIsApplePayAvailable from '@/components/Apple/useIsApplePayAvailable';
import RestrictionsTooltip from '@/components/UI/RestrictionsTooltip';
import styles from './ConnectionMethod.module.scss';
import { useConvenienceFeesProvider } from '@/providers/ConvenienceFees.provider';
import { ConvenienceFeeBreakdown, AcceptedCardBrandsTooltip, Button, Title, Logos } from 'UI';
import { useBank } from '@/providers/Bank.provider';
import { getBankingRailOptions } from '@/utils/bankingRailMapping';
import vopayLogo from './Icons/vopayLogo.svg';
import radioChecked from './Icons/radioChecked.svg';
import radioUnchecked from './Icons/radioUnchecked.svg';

type LocationProp = {
  paymentMethod: string;
  origin: string;
};

type MethodOption = {
  id: string;
  label: ReactNode;
  caption: ReactNode;
  // Runs on Continue: the radio only picks the method, it never navigates by itself
  select: () => void;
};

type DigitalWallet = {
  method: Extract<ELinxConnectionMethod, 'paypal' | 'venmo' | 'zelle'>;
  name: 'PayPal' | 'Venmo' | 'Zelle';
  isAvailable: boolean;
};

const classes = withStyles(styles);

const TITLE_ID = 'choose-payment-method-title';

/** A method's delivery time and fee, as the design's Body/13 caption. */
function FeeCaption({
  method,
  bankingPaymentRail,
}: {
  method: ELinxConnectionMethod;
  bankingPaymentRail?: BankingPaymentRail;
}) {
  return <ConvenienceFeeBreakdown connectionMethod={method} bankingPaymentRail={bankingPaymentRail} compact />;
}

/** A card method's label, with the card brands it accepts. */
function CardLabel({
  text,
  acceptedCardBrands,
}: {
  text: string;
  acceptedCardBrands: ComponentProps<typeof AcceptedCardBrandsTooltip>['acceptedCardBrands'];
}) {
  return (
    <span className={classes('labelWithTooltip')}>
      {text}
      <AcceptedCardBrandsTooltip acceptedCardBrands={acceptedCardBrands} position="topRight" />
    </span>
  );
}

export default function ConnectionMethod() {
  const history = useHistory();
  const { setLayoutInfo } = useLayout();
  const { state } = useLocation<LocationProp>();
  const { token, isSetByBrowser } = useToken();
  const { flow, paylink } = usePaylink();
  const { setBankAccountRail } = useBank();
  const { t, i18n } = useTranslation('translations', { keyPrefix: 'PreferredMethod' });
  const { t: tConvenience } = useTranslation('translations', { keyPrefix: 'ConvenienceFee' });
  const { showFees, convenienceFees, getProcessingFee, getLowestAndHighestFees } = useConvenienceFeesProvider();
  const isApplePayAvailable = useIsApplePayAvailable();
  const [selectedId, setSelectedId] = useState<string>();

  const bankRailOptions = useMemo(() => {
    if (!paylink.BankAccountRail || !Array.isArray(paylink.BankAccountRail)) {
      return [];
    }

    return getBankingRailOptions(paylink.BankAccountRail);
  }, [paylink.BankAccountRail]);

  const regionType = useMemo(() => {
    const isCanada = token?.Country?.toLowerCase() === 'canada' || token?.Country?.toLowerCase() === 'ca';

    return isCanada
      ? tConvenience('someProvinces', { defaultValue: 'some provinces' })
      : tConvenience('someStates', { defaultValue: 'some states' });
  }, [token?.Country]);

  const hasRestrictionsForCountry = useMemo(() => {
    if (!convenienceFees?.Restrictions || !token?.Country) return false;

    return convenienceFees.Restrictions.some(
      (restriction: ConvenienceFeeRestriction) =>
        restriction.CountryAbbreviation?.toLowerCase() === token.Country?.toLowerCase(),
    );
  }, [convenienceFees?.Restrictions, token?.Country]);

  function isPaymentMethodFree(paymentMethod: ELinxConnectionMethod) {
    const breakdown = getProcessingFee({
      paymentMethod,
    });

    if (!breakdown) return true;

    return !breakdown.hasFees;
  }

  useEffect(() => {
    setLayoutInfo(
      chooseColor<string | null | undefined>('#FFD147', token?.BrandingDetails?.Branding?.PrimaryColor),
      'sun',
      '',
      70,
      32,
      0,
      false,
      false,
    );
  }, []);

  function isEnabled(method: string) {
    return String(state?.paymentMethod) === 'any' || String(state?.paymentMethod).includes(method);
  }

  function goTo(pathname: string) {
    return () => history.push({ pathname, state: { origin: state?.origin } });
  }

  const options: MethodOption[] = [];

  if (isEnabled('bank')) {
    const bankLabel = t('bankButton', { defaultValue: 'Bank Account' });

    if (bankRailOptions.length > 1) {
      bankRailOptions.forEach((option) => {
        options.push({
          id: `bank-${option.rail}`,
          label: `${bankLabel} · ${option.label}`,
          caption: <FeeCaption method="bank" bankingPaymentRail={option.rail} />,
          select: () => {
            setBankAccountRail(option.rail);
            history.push('/connect/processor/bank');
          },
        });
      });
    } else {
      options.push({
        id: 'bank',
        label: bankLabel,
        caption: <FeeCaption method="bank" bankingPaymentRail={paylink?.BankAccountRail?.[0]} />,
        select: () => {
          setBankAccountRail(paylink?.BankAccountRail[0]);
          history.push('/connect/processor/bank');
        },
      });
    }
  }

  const acceptedCardBrands = token?.AcceptedCardBrands;

  if (isEnabled('credit') && isEnabled('debitcard') && token?.CardTypeValidation === false) {
    options.push({
      id: 'credit',
      label: (
        <CardLabel
          text={t('creditDebitCardButton', { defaultValue: 'Credit/Debit Card' })}
          acceptedCardBrands={acceptedCardBrands}
        />
      ),
      caption: <FeeCaption method="credit" />,
      select: goTo('/connect/processor/credit'),
    });
  } else {
    if (isEnabled('credit')) {
      options.push({
        id: 'credit',
        label: (
          <CardLabel
            text={t('creditCardButton', { defaultValue: 'Credit Card' })}
            acceptedCardBrands={acceptedCardBrands}
          />
        ),
        caption: <FeeCaption method="credit" />,
        select: goTo('/connect/processor/credit'),
      });
    }

    if (isEnabled('debitcard')) {
      options.push({
        id: 'debitcard',
        label: (
          <CardLabel
            text={t('pushToCardButton', { defaultValue: 'Debit Card' })}
            acceptedCardBrands={acceptedCardBrands}
          />
        ),
        caption: <FeeCaption method="debitcard" />,
        select: goTo('/connect/processor/debitcard'),
      });
    }
  }

  if (isEnabled('email')) {
    options.push({
      id: 'email',
      label:
        flow === Flow.CONNECT ? (
          t('emailButton', { defaultValue: 'Email/Phone Number' })
        ) : (
          <InteracWordmark
            type={flow === Flow.FUND__SINGLE || flow === Flow.FUND__SCHEDULED ? 'moneyRequest' : 'eTransfer'}
          />
        ),
      caption: <FeeCaption method="email" />,
      select: goTo('/connect/processor/email'),
    });
  }

  const digitalWallets: DigitalWallet[] = [
    { method: 'paypal', name: 'PayPal', isAvailable: true },
    { method: 'venmo', name: 'Venmo', isAvailable: token?.Country === 'US' },
    { method: 'zelle', name: 'Zelle', isAvailable: token?.Country === 'US' },
  ];

  digitalWallets.forEach(({ method, name, isAvailable }) => {
    if (isEnabled(method) && isAvailable) {
      options.push({
        id: method,
        label: name,
        caption: <FeeCaption method={method} />,
        select: () => history.push(`/connect/digital-wallet/${method}`),
      });
    }
  });

  const selected = options.find((option) => option.id === selectedId) ?? options[0];

  const cardMethods: ELinxConnectionMethod[] = ['credit', 'debitcard'];

  const showFeesWaivedNote =
    showFees &&
    hasRestrictionsForCountry &&
    cardMethods.some((method) => options.some((option) => option.id === method) && !isPaymentMethodFree(method));

  const showGooglePay = isEnabled('googlepay');
  const showApplePay = isEnabled('applepay') && isApplePayAvailable;

  // One caption can speak for both wallet buttons only when each would show the same fee
  function walletFeeKey(method: 'googlepay' | 'applepay') {
    const fee = getProcessingFee({ paymentMethod: method });
    const { lowest, highest } = getLowestAndHighestFees(method);

    return JSON.stringify([fee?.processingFee, fee?.transactionFee, lowest, highest]);
  }

  const showSharedWalletFee = showGooglePay && showApplePay && walletFeeKey('googlepay') === walletFeeKey('applepay');
  const language = i18n.language?.startsWith('fr') ? 'fr' : 'en';
  const walletNames = [showApplePay && 'Apple Pay', showGooglePay && 'Google Pay'].filter(Boolean).join(' / ');

  // A wallet-only request names its wallets; until a wallet button shows (Apple Pay is still checking,
  // or the browser has none) there is nothing to name, so no subtitle at all
  const subtitle =
    options.length > 0
      ? t('methodSubtitle', { defaultValue: 'Select how you want to complete this request.' })
      : walletNames &&
        t('walletOnlySubtitle', {
          wallet: walletNames,
          defaultValue: `Pay with ${walletNames}. Your card details are not shared with the sender.`,
        });

  return (
    <div className={classes('page')}>
      <main className={classes('card')}>
        <div className={classes('topRow')}>
          {token?.BrandingDetails?.Logo ? (
            <img className={classes('logo')} src={token.BrandingDetails.Logo} alt="Logo" />
          ) : (
            <img className={classes('logo')} src={vopayLogo} alt="VoPay" width={115} height={30} />
          )}

          <div className={classes('languageSwitcher')}>
            {['en', 'fr'].map((code, index) => (
              <span key={code} className={classes('languageItem')}>
                {index > 0 && <span aria-hidden>|</span>}

                <button
                  type="button"
                  className={classes('languageButton', language === code && 'active')}
                  aria-pressed={language === code}
                  onClick={() => i18n.changeLanguage(code)}
                >
                  {code === 'en' ? 'En' : 'Fr'}
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* In an in-app browser opened from an email or SMS there may be no browser Back, so the
            page keeps its own way back to the request summary, as IframeLayout's arrow did */}
        {!isSetByBrowser && (
          <button type="button" className={classes('back')} onClick={() => history.goBack()}>
            <BodyText as="span" size="sm">
              {t('back', { defaultValue: 'Back' })}
            </BodyText>
          </button>
        )}

        <div className={classes('heading')}>
          {/* H3/Emphasized */}
          <Title level="3" id={TITLE_ID} className={classes('title')}>
            {t('methodTitle', { defaultValue: 'Choose Payment Method' })}
          </Title>

          {/* Body/13/Regular */}
          {subtitle && (
            <BodyText size="sm" className={classes('secondaryText')}>
              {subtitle}
            </BodyText>
          )}
        </div>

        {options.length > 0 && (
          <>
            <div className={classes('methods')} role="radiogroup" aria-labelledby={TITLE_ID}>
              {options.map((option) => {
                const inputId = `payment-method-${option.id}`;
                const captionId = `${inputId}-caption`;

                return (
                  <div key={option.id} className={classes('method')}>
                    {/* htmlFor, not nesting alone: a label activates its first control, and the card
                        rows hold the accepted-brands tooltip button before the radio */}
                    <label className={classes('field')} htmlFor={inputId}>
                      {/* Body/16/Regular, as a span: a label can't hold BodyText's default div */}
                      <BodyText as="span" className={classes('label')}>
                        {option.label}
                      </BodyText>

                      <input
                        id={inputId}
                        type="radio"
                        name="paymentMethod"
                        className={classes('radioInput')}
                        aria-describedby={captionId}
                        checked={selected?.id === option.id}
                        onChange={() => setSelectedId(option.id)}
                      />

                      <img
                        className={classes('radio')}
                        src={selected?.id === option.id ? radioChecked : radioUnchecked}
                        alt=""
                        width={16}
                        height={16}
                      />
                    </label>

                    <div id={captionId}>
                      <BodyText size="sm" className={classes('helperText')}>
                        {option.caption}
                      </BodyText>
                    </div>
                  </div>
                );
              })}
            </div>

            {showFeesWaivedNote && (
              <div className={classes('note')}>
                <BodyText size="sm" className={classes('secondaryText')}>
                  {t('feesWaivedNote', {
                    regionType,
                    defaultValue: `Fees may be waived in ${regionType}. The fee is shown again before you confirm.`,
                  })}
                </BodyText>

                <RestrictionsTooltip
                  restrictions={convenienceFees?.Restrictions || []}
                  country={token?.Country}
                  position="right"
                />
              </div>
            )}

            <Button colorScheme="primary" className={classes('continue')} onClick={() => selected?.select()}>
              {t('continue', { defaultValue: 'Continue' })}
            </Button>
          </>
        )}

        {(showGooglePay || showApplePay) && (
          // Google's and Apple's brand rules: their own visible button is what opens the payment sheet
          <>
            {options.length > 0 && (
              <div className={classes('divider')}>
                <BodyText size="sm" className={classes('secondaryText')}>
                  {t('orPayWith', { defaultValue: 'or pay with' })}
                </BodyText>
              </div>
            )}

            <div className={classes('wallets')} data-testid="wallet-buttons">
              {showApplePay && (
                <>
                  <ApplePayButton className={classes('walletButton')} />

                  {!showSharedWalletFee && (
                    <BodyText size="sm" className={classes('secondaryText', 'walletCaption')}>
                      <FeeCaption method="applepay" />
                    </BodyText>
                  )}
                </>
              )}

              {showGooglePay && (
                <>
                  <GooglePayButton className={classes('walletButton')} />

                  <BodyText size="sm" className={classes('secondaryText', 'walletCaption')}>
                    <FeeCaption method="googlepay" />
                  </BodyText>
                </>
              )}
            </div>
          </>
        )}
      </main>

      <div className={classes('footer')}>
        <Term
          term={`${t('secure', {
            defaultValue:
              'This is a secure page and your information will be encrypted. Payment method information will not be saved.',
          })}`}
          center
        />

        <Logos />
      </div>
    </div>
  );
}
